import {
  normalizeGroupTalkMemberId,
  normalizeGroupTalkStanceId,
  type GroupTalkDraftMode,
  type GroupTalkParticipant,
  type GroupTalkTemplateId,
} from "./groupTalk";
import type {
  CreateGroupTalkTurnParams,
  KnowledgeAiPreparationStatus,
  KnowledgeFailureReason,
  KnowledgeItemStatus,
} from "./api";
import { getKnowledgeItemDisplayName } from "./knowledge";
import type { GroupTalkSessionView, GroupTalkTurnView } from "./groupTalkView";

const GROUP_TALK_DRAFT_KEY = "wizmate.group-talk.draft.v1";
const GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX = "wizmate.group-talk.session.v1:";

export type GroupTalkDraft = {
  templateId: GroupTalkTemplateId;
  title: string;
  description: string;
  participants: GroupTalkParticipant[];
  mode?: GroupTalkDraftMode;
  consultationText?: string;
  recommendationReason?: string;
  recommendationConfidence?: string;
  memberEditingLocked?: boolean;
  knowledgeAttachments?: GroupTalkKnowledgeAttachmentDraft[];
  /** @deprecated 旧sessionStorage / route stateの読み取り互換専用。新規保存では使用しない。 */
  knowledgeAttachment?: GroupTalkKnowledgeAttachmentDraft | null;
};

const GROUP_TALK_KNOWLEDGE_ATTACHMENT_TYPES = [
  "pdf",
  "executive_memo",
  "url",
  "company_research",
  "deep_report",
] as const;

export type GroupTalkKnowledgeAttachmentType = (typeof GROUP_TALK_KNOWLEDGE_ATTACHMENT_TYPES)[number];

function isGroupTalkKnowledgeAttachmentType(value: unknown): value is GroupTalkKnowledgeAttachmentType {
  return GROUP_TALK_KNOWLEDGE_ATTACHMENT_TYPES.some((type) => type === value);
}

export type GroupTalkKnowledgeAttachmentDraft = {
  id: string;
  type?: GroupTalkKnowledgeAttachmentType;
  title: string;
  originalFilename?: string | null;
  status?: KnowledgeItemStatus;
  failureReason?: KnowledgeFailureReason | string | null;
  pageCount?: number | null;
  textExtractable?: boolean | null;
  aiProcessingAllowed?: boolean | null;
  aiPreparationStatus?: KnowledgeAiPreparationStatus | null;
  hasSummary?: boolean;
  summarizedAt?: string | null;
};

export function isGroupTalkKnowledgeAttachmentDraftUsable(
  item: GroupTalkKnowledgeAttachmentDraft | null | undefined
): boolean {
  if (!item) return false;
  const type = item.type ?? "pdf";
  if (!isGroupTalkKnowledgeAttachmentType(type)) return false;
  if ((item.status ?? "ready") !== "ready" || item.failureReason) return false;

  if (type === "url") {
    return item.textExtractable === true
      && item.aiProcessingAllowed === true
      && (item.aiPreparationStatus === "ready" || item.aiPreparationStatus == null);
  }

  const textExtractable = item.textExtractable === undefined ? true : item.textExtractable === true;
  const aiProcessingAllowed = item.aiProcessingAllowed === undefined ? true : item.aiProcessingAllowed === true;
  return textExtractable && aiProcessingAllowed;
}

export type GroupTalkActiveSessionSnapshot = {
  sessionId: string;
  draft: GroupTalkDraft;
  session: GroupTalkSessionView;
  turns: GroupTalkTurnView[];
  initialUserMessage?: string;
  initialUserMessageSent?: boolean;
  pendingOperation?: GroupTalkPendingOperation;
};

export type GroupTalkPendingOperation = {
  sessionId: string;
  clientRequestId: string;
  requestType: "intake" | "turn";
  userMessage: string;
  selectedChoice?: CreateGroupTalkTurnParams["selectedChoice"];
  startedAt: string;
  completionTurnId?: string;
  completionStartedAt?: string;
};

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isValidParticipant(value: unknown): value is GroupTalkParticipant {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<GroupTalkParticipant>;
  return (
    typeof item.participantId === "string" &&
    typeof item.memberId === "string" &&
    (typeof item.stanceId === "string" || item.stanceId === null) &&
    typeof item.fixed === "boolean"
  );
}

function isValidDraft(value: unknown): value is GroupTalkDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Partial<GroupTalkDraft>;
  return (
    typeof draft.templateId === "string" &&
    typeof draft.title === "string" &&
    typeof draft.description === "string" &&
    Array.isArray(draft.participants) &&
    draft.participants.every((participant) => isValidParticipant(participant))
  );
}

function canUseLegacyReadyKnowledgeFlags(status: KnowledgeItemStatus | undefined, type: string): boolean {
  const effectiveStatus = status ?? "ready";
  return effectiveStatus === "ready" && (type === "pdf" || type === "executive_memo");
}

function hasOwnProperty(value: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function normalizeStoredKnowledgeFlag(
  item: Partial<GroupTalkKnowledgeAttachmentDraft>,
  key: "textExtractable" | "aiProcessingAllowed",
  legacyReadyFlags: boolean
): boolean | null {
  if (hasOwnProperty(item, key)) {
    const value = item[key];
    return typeof value === "boolean" ? value : null;
  }
  return legacyReadyFlags ? true : null;
}

function normalizeStoredKnowledgeAttachment(value: unknown): GroupTalkKnowledgeAttachmentDraft | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<GroupTalkKnowledgeAttachmentDraft>;
  if (typeof item.id !== "string" || item.id.trim() === "") return null;
  const storedType = typeof item.type === "string" ? item.type.trim() : "";
  const type = storedType || "pdf";
  if (!isGroupTalkKnowledgeAttachmentType(type)) return null;
  const fallbackTitle = type === "executive_memo"
    ? "社長メモ"
    : type === "url"
      ? "Webページ"
      : type === "company_research"
        ? "企業リサーチ"
        : type === "deep_report"
          ? "深掘りレポート"
          : "PDF資料";
  const originalFilename = typeof item.originalFilename === "string" ? item.originalFilename : null;
  const title = getKnowledgeItemDisplayName({
    type,
    title: typeof item.title === "string" ? item.title : fallbackTitle,
    original_filename: originalFilename,
  });
  const status = item.status;
  const legacyReadyFlags = canUseLegacyReadyKnowledgeFlags(status, type);
  return {
    id: item.id,
    type,
    title,
    originalFilename,
    status: status ?? (legacyReadyFlags ? "ready" : undefined),
    failureReason: typeof item.failureReason === "string" ? item.failureReason : null,
    pageCount: typeof item.pageCount === "number" ? item.pageCount : null,
    textExtractable: normalizeStoredKnowledgeFlag(item, "textExtractable", legacyReadyFlags),
    aiProcessingAllowed: normalizeStoredKnowledgeFlag(item, "aiProcessingAllowed", legacyReadyFlags),
    aiPreparationStatus:
      item.aiPreparationStatus === "processing"
      || item.aiPreparationStatus === "ready"
      || item.aiPreparationStatus === "failed"
        ? item.aiPreparationStatus
        : null,
    hasSummary: Boolean(item.hasSummary),
    summarizedAt: typeof item.summarizedAt === "string" ? item.summarizedAt : null,
  };
}

function normalizeStoredParticipant(participant: GroupTalkParticipant): GroupTalkParticipant {
  const memberId = normalizeGroupTalkMemberId(participant.memberId);
  const { displayName: _displayName, personaKey: _personaKey, avatarKey: _avatarKey, imageSrc: _imageSrc, ...rest } = participant;
  const displayName = typeof participant.displayName === "string" && participant.displayName.trim() !== ""
    ? participant.displayName.trim()
    : undefined;
  const personaKey = typeof participant.personaKey === "string" && participant.personaKey.trim() !== ""
    ? participant.personaKey.trim()
    : undefined;
  const avatarKey = typeof participant.avatarKey === "string" && participant.avatarKey.trim() !== ""
    ? participant.avatarKey.trim()
    : undefined;
  const imageSrc = typeof participant.imageSrc === "string" && participant.imageSrc.trim() !== ""
    ? participant.imageSrc.trim()
    : undefined;
  return {
    ...rest,
    memberId,
    stanceId: memberId === "mei" ? null : normalizeGroupTalkStanceId(participant.stanceId),
    fixed: memberId === "mei" ? true : participant.fixed,
    ...(displayName ? { displayName } : {}),
    ...(personaKey ? { personaKey } : {}),
    ...(avatarKey ? { avatarKey } : {}),
    ...(imageSrc ? { imageSrc } : {}),
    imageVariant: participant.imageVariant === 2 ? 2 : participant.imageVariant === 1 ? 1 : undefined,
  };
}

function normalizeStoredKnowledgeAttachments(draft: GroupTalkDraft): GroupTalkKnowledgeAttachmentDraft[] {
  const source = Object.prototype.hasOwnProperty.call(draft, "knowledgeAttachments")
    ? (Array.isArray(draft.knowledgeAttachments) ? draft.knowledgeAttachments : [])
    : draft.knowledgeAttachment
      ? [draft.knowledgeAttachment]
      : [];
  const normalized = source
    .map((item) => normalizeStoredKnowledgeAttachment(item))
    .filter((item): item is GroupTalkKnowledgeAttachmentDraft => item !== null);

  return normalized.filter(
    (item, index) => index === normalized.findIndex((candidate) => candidate.id === item.id)
  ).slice(0, 3);
}

export function normalizeGroupTalkDraft(draft: GroupTalkDraft): GroupTalkDraft {
  const { knowledgeAttachment: _legacyKnowledgeAttachment, ...canonicalDraft } = draft;
  return {
    ...canonicalDraft,
    mode: draft.mode === "recommend" || draft.mode === "template" || draft.mode === "custom" ? draft.mode : undefined,
    consultationText: typeof draft.consultationText === "string" ? draft.consultationText : "",
    recommendationReason: typeof draft.recommendationReason === "string" ? draft.recommendationReason : "",
    recommendationConfidence: typeof draft.recommendationConfidence === "string" ? draft.recommendationConfidence : "",
    memberEditingLocked: Boolean(draft.memberEditingLocked),
    knowledgeAttachments: normalizeStoredKnowledgeAttachments(draft),
    participants: draft.participants.map((participant) => normalizeStoredParticipant(participant)),
  };
}

function normalizeStoredSessionView(
  session: GroupTalkSessionView,
  draft: GroupTalkDraft
): GroupTalkSessionView {
  const legacySession = session as GroupTalkSessionView & {
    knowledgeAttachment?: GroupTalkSessionView["knowledgeAttachments"][number] | null;
  };
  const source = Object.prototype.hasOwnProperty.call(legacySession, "knowledgeAttachments")
    ? (Array.isArray(legacySession.knowledgeAttachments) ? legacySession.knowledgeAttachments : [])
    : legacySession.knowledgeAttachment
      ? [legacySession.knowledgeAttachment]
      : [];
  const knowledgeAttachments = source.filter(
    (item, index) => item
      && typeof item.id === "string"
      && item.id.trim() !== ""
      && index === source.findIndex((candidate) => candidate?.id === item.id)
  ).slice(0, 3).map((item) => ({
    ...item,
    type: isGroupTalkKnowledgeAttachmentType(item.type)
      ? item.type
      : draft.knowledgeAttachments?.find((candidate) => candidate.id === item.id)?.type ?? "pdf",
  }));
  const { knowledgeAttachment: _legacyAttachment, ...canonicalSession } = legacySession;

  return {
    ...canonicalSession,
    knowledgeAttachments,
  };
}

function activeSessionKey(sessionId: string) {
  return `${GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX}${sessionId}`;
}

function normalizeStoredPendingOperation(
  value: unknown,
  sessionId: string
): GroupTalkPendingOperation | undefined {
  if (!value || typeof value !== "object") return undefined;
  const operation = value as Partial<GroupTalkPendingOperation>;
  if (
    operation.sessionId !== sessionId ||
    typeof operation.clientRequestId !== "string" ||
    (operation.requestType !== "intake" && operation.requestType !== "turn") ||
    typeof operation.userMessage !== "string" ||
    typeof operation.startedAt !== "string"
  ) {
    return undefined;
  }

  return {
    sessionId,
    clientRequestId: operation.clientRequestId,
    requestType: operation.requestType,
    userMessage: operation.userMessage,
    ...(operation.selectedChoice ? { selectedChoice: operation.selectedChoice } : {}),
    startedAt: operation.startedAt,
    ...(typeof operation.completionTurnId === "string" && typeof operation.completionStartedAt === "string"
      ? {
          completionTurnId: operation.completionTurnId,
          completionStartedAt: operation.completionStartedAt,
        }
      : {}),
  };
}

export function createGroupTalkClientRequestId(): string {
  const cryptoApi = globalThis.crypto as (Crypto & { randomUUID?: () => string }) | undefined;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    // jsdom等の非browser環境向け。対応browserでは上のWeb Crypto経路を使用する。
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256);
    });
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

export function loadGroupTalkDraft(): GroupTalkDraft | null {
  if (!canUseSessionStorage()) return null;
  const parsed = safeParseJson<unknown>(window.sessionStorage.getItem(GROUP_TALK_DRAFT_KEY));
  return isValidDraft(parsed) ? normalizeGroupTalkDraft(parsed) : null;
}

export function saveGroupTalkDraft(draft: GroupTalkDraft) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(GROUP_TALK_DRAFT_KEY, JSON.stringify(normalizeGroupTalkDraft(draft)));
}

export function clearGroupTalkDraft() {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(GROUP_TALK_DRAFT_KEY);
}

export function loadGroupTalkActiveSessionSnapshot(sessionId: string): GroupTalkActiveSessionSnapshot | null {
  if (!canUseSessionStorage()) return null;
  const snapshot = safeParseJson<GroupTalkActiveSessionSnapshot>(window.sessionStorage.getItem(activeSessionKey(sessionId)));
  if (!snapshot) return null;
  const draft = normalizeGroupTalkDraft(snapshot.draft);
  return {
    ...snapshot,
    draft,
    session: normalizeStoredSessionView(snapshot.session, draft),
    pendingOperation: normalizeStoredPendingOperation(snapshot.pendingOperation, sessionId),
  };
}

export function saveGroupTalkActiveSessionSnapshot(snapshot: GroupTalkActiveSessionSnapshot) {
  if (!canUseSessionStorage()) return;
  const draft = normalizeGroupTalkDraft(snapshot.draft);
  window.sessionStorage.setItem(activeSessionKey(snapshot.sessionId), JSON.stringify({
    ...snapshot,
    draft,
    session: normalizeStoredSessionView(snapshot.session, draft),
  }));
}

export function clearGroupTalkActiveSessionSnapshot(sessionId: string) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(activeSessionKey(sessionId));
}

export function clearAllGroupTalkActiveSessionSnapshots() {
  if (!canUseSessionStorage()) return;
  const keys = Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index));
  keys.forEach((key) => {
    if (key?.startsWith(GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX)) {
      window.sessionStorage.removeItem(key);
    }
  });
}
